import { DestroyRef, computed, inject } from '@angular/core';
import { signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { updateState } from '@angular-architects/ngrx-toolkit';
import { BrnDialogService } from '@spartan-ng/brain/dialog';
import { ToastService } from '@lite-workspace/shared/helm-ui/toast';
import { firstValueFrom, take } from 'rxjs';
import {
  FileAlreadyExistsDialogComponent,
  isHeicFile,
  sortFilesNaturally,
  type FileAlreadyExistsContext,
  type FileAlreadyExistsResult,
  UploadEngine,
} from '@lite-workspace/uploader-core';
import { GALLERY_UPLOAD_DATA, REGISTER_BATCH_LIMIT } from '../infrastructure/gallery-upload.data';
import { GALLERY_MANAGE_DATA } from '../infrastructure/gallery-manage.data';
import {
  UPLOAD_FAILURE_MESSAGES,
  fileModifiedAtPayload,
  isRawFile,
  isVideoFile,
  uploadFailureSeverity,
  validateUploadFile,
  validateVideoDimensions,
} from '../entities/upload.models';
import type {
  PendingUploadTile,
  RegisterOutcome,
  SyncResult,
  UploadFailureReason,
  UploadItem,
  UploadItemStatus,
} from '../entities/upload.models';
import { GalleryManageStore } from './gallery-manage.store';
import { environment } from '@environment';

export type UploadVideoResult = { ok: true; fileUrl: string; fileId: string } | { ok: false; reason: UploadFailureReason };

const POLL_INTERVAL_MS = 2500;
const POLL_MAX_PASSES = 240;
// EXIF-backfill drain: the server backfills at most one bounded batch per sync call, so
// a gallery with a pre-migration backlog needs repeated calls with no upload in flight.
// 40 passes × the server's 300-row batch covers a 12k-photo gallery in one open.
const EXIF_DRAIN_MAX_PASSES = 40;
const EXIF_DRAIN_INTERVAL_MS = 1000;

interface UploadQueueState {
  galleryId: string | null;
  items: UploadItem[];
  /** Фото пачки, уже заведённые сервером: их плитки в сетке проявляются, старые — нет. */
  landedPhotoIds: string[];
  /** Фото пачки, чью картинку сетка уже нарисовала — гейт финала полосы (спека п. 7, п. 29). */
  paintedPhotoIds: string[];
  // Bumps once per poll pass that flipped at least one photo to ready — the manage
  // page watches it to re-read the scenes (show-on-first-thumb, lab parity).
  readyPulse: number;
  generation: number;
  // Bytes of the batch already on the wire, 0–100; `null` outside the sending phase,
  // when the bar has nothing but its file counter to show.
  percent: number | null;
  /** Real bytes of the batch — the bar's counter shows «155 из 220 МБ», not a percent. */
  bytesSent: number;
  bytesTotal: number;
  /** `navigator.onLine` — the bar shows «Нет связи» while it is false (спека п. 15). */
  online: boolean;
}

const initialState: UploadQueueState = {
  galleryId: null,
  items: [],
  landedPhotoIds: [],
  paintedPhotoIds: [],
  readyPulse: 0,
  generation: 0,
  percent: null,
  bytesSent: 0,
  bytesTotal: 0,
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
};

function wait(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// Кадр из выбранного файла живёт ровно столько, сколько его плитка: адрес объекта держит
// сам файл в памяти, и пачка на 20 снимков без отзыва оставила бы там сотни мегабайт.
function dropPreviews(items: readonly UploadItem[]): void {
  for (const item of items) if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
}

function localPreview(file: File): string | undefined {
  return isVideoFile(file) || isHeicFile(file) ? undefined : URL.createObjectURL(file);
}

function isSettled(item: UploadItem): boolean {
  return item.status === 'ready' || item.status === 'failed';
}

/** Исход броска файлов: принят в очередь (в т.ч. пустой или отбракованный целиком) или отвергнут, потому
 *  что полоса занята пачкой ДРУГОЙ галереи — тогда файлы не тронуты и вызывающий говорит об этом. */
export type UploadBatchOutcome = 'queued' | 'busy_other_gallery';

export const UploadQueueStore = signalStore(
  { providedIn: 'root' },
  environment.storeWithDevTools('uploadQueue'),
  withState<UploadQueueState>(initialState),
  withComputed(store => ({
    active: computed(() =>
      store.items().some(item => item.status === 'uploading' || item.status === 'registering' || item.status === 'processing'),
    ),
    readyCount: computed(() => store.items().filter(item => item.status === 'ready').length),
    failedCount: computed(() => store.items().filter(item => item.status === 'failed').length),
    totalCount: computed(() => store.items().length),
    paintedCount: computed(() => store.paintedPhotoIds().length),
    // Хвост пачки после «+» и уже загруженных фото: готовая строка уходит сразу — её слот
    // занимает плитка самого фото. Строку, чьё фото сетка уже показывает, прячет сама сетка.
    pendingTiles: computed<PendingUploadTile[]>(() =>
      store
        .items()
        .filter(item => item.status !== 'ready')
        .map(item => {
          const failed = item.status === 'failed';
          return {
            fileId: item.fileId,
            name: item.name,
            sceneId: item.sceneId ?? null,
            photoId: item.photoId ?? null,
            previewUrl: item.previewUrl ?? null,
            failed,
            severity: failed ? uploadFailureSeverity(item.failure) : null,
            hint: failed ? UPLOAD_FAILURE_MESSAGES[item.failure ?? 'unknown'] : null,
          };
        }),
    ),
  })),
  withMethods(
    (
      store,
      data = inject(GALLERY_UPLOAD_DATA),
      manage = inject(GalleryManageStore),
      manageData = inject(GALLERY_MANAGE_DATA),
      dialog = inject(BrnDialogService),
      destroyRef = inject(DestroyRef),
      toast = inject(ToastService),
      engine = inject(UploadEngine),
    ) => {
      if (typeof window !== 'undefined') {
        window.addEventListener('online', () => updateState(store, '[UploadQueue] Online', { online: true }));
        window.addEventListener('offline', () => updateState(store, '[UploadQueue] Offline', { online: false }));
      }

      // Закрытие или перезагрузка вкладки посреди пачки теряет неотправленные файлы — браузер переспросит.
      // `active()` covers register and processing too, steps the engine knows nothing about.
      destroyRef.onDestroy(engine.guardUnload(() => store.active()));

      const setItemStatus = (tag: string, fileIds: readonly string[], status: UploadItemStatus): void => {
        const ids = new Set(fileIds);
        updateState(store, tag, {
          items: store.items().map(item => (ids.has(item.fileId) ? { ...item, status } : item)),
        });
      };

      const failItems = (tag: string, fileIds: readonly string[], reason: UploadFailureReason): void => {
        const ids = new Set(fileIds);
        // A duplicate is already in the gallery: the row settles done, silently. The page may not
        // show that photo yet (another tab, an unopened scene) — the pulse makes it re-read the scenes.
        if (reason === 'duplicate') {
          for (const fileId of fileIds) replaceTargets.delete(fileId);
          updateState(store, tag, {
            items: store.items().map(item => (ids.has(item.fileId) ? { ...item, status: 'ready' as const } : item)),
            paintedPhotoIds: [...store.paintedPhotoIds(), ...fileIds],
            readyPulse: store.readyPulse() + 1,
          });
          return;
        }

        if (reason === 'processing') for (const fileId of fileIds) replaceTargets.delete(fileId);

        updateState(store, tag, {
          items: store
            .items()
            .map(item => (ids.has(item.fileId) ? { ...item, status: 'failed' as const, failure: reason } : item)),
        });
      };

      /**
       * Одно окно «уже есть в галерее» ДО очереди: имена брошенных файлов против имён уже
       * загруженных фото галереи — фотограф отвечает один раз на всю пачку, как в Фотоклассе.
       * Сервер узнаёт про дубль только на регистрации, и его `duplicate` остаётся запасным
       * ответом на имена, которых клиент ещё не видел (сцена не открыта, фото пришло из другой вкладки).
       * Возвращает файлы, которые идут в очередь; пусто — бросок отменён.
       */
      const knownNames = (): ReadonlySet<string> =>
        new Set(
          manage
            .scenes()
            .flatMap(scene => scene.photos.map(photo => photo.originalName))
            .filter((name): name is string => Boolean(name)),
        );

      const askAboutDuplicates = async (files: readonly File[], known: ReadonlySet<string>): Promise<readonly File[]> => {
        const duplicates = files.filter(file => known.has(file.name));
        const fresh = files.filter(file => !known.has(file.name));
        const context: FileAlreadyExistsContext = { duplicates: duplicates.map(file => file.name), fresh: fresh.length };
        const ref = dialog.open<FileAlreadyExistsContext, FileAlreadyExistsResult>(
          FileAlreadyExistsDialogComponent,
          undefined,
          context,
          { hasBackdrop: true },
        );
        const verdict = await firstValueFrom(ref.closed$.pipe(take(1)), { defaultValue: undefined });
        // «Перезаписать» — пачка идёт целиком; старое фото того же имени уходит, когда новое обработано.
        if (verdict === 'replace') return files;
        return verdict === 'skip' ? fresh : [];
      };

      // photoId → fileId of the current batch; poll results arrive keyed by photoId.
      const photoToFile = new Map<string, string>();
      /** The project the live batch uploads into: cancel aborts what it has on the wire. */
      let uploadKey: string | null = null;

      // fileId → the picked File, so «Повторить всё» can re-send without asking for the files again.
      // A File is a handle to disk, not its bytes, so holding the batch costs nothing.
      const retryable = new Map<string, File>();

      // fileId → photos of the gallery that file replaces («Перезаписать»). They leave only when the new
      // photo is processed: a broken file or a failed processing keeps the old one. Kept through an upload
      // failure, so «Повторить всё» carries the intent.
      const replaceTargets = new Map<string, readonly string[]>();

      const retireReplaced = async (galleryId: string, fileIds: readonly string[]): Promise<void> => {
        const stale = fileIds.flatMap(fileId => replaceTargets.get(fileId) ?? []);
        for (const fileId of fileIds) replaceTargets.delete(fileId);
        if (stale.length === 0) return;
        const result = await manageData
          .deletePhotos(galleryId, stale, true)
          .catch(() => ({ ok: false as const, reason: 'error' as const }));
        if (!result.ok) {
          toast.error(
            stale.length === 1
              ? 'Старое фото не удалилось — в галерее обе версии'
              : 'Старые фото не удалились — в галерее обе версии',
          );
          return;
        }

        // RACE-SAFE: the pulse only asks the page to re-read the scenes; a late bump costs one extra read.
        updateState(store, '[UploadQueue] Replaced Photos Deleted', { readyPulse: store.readyPulse() + 1 });
      };

      // Один признак повтора на окно отказов и на «Повторить всё»: файл, ни разу не принятый
      // к отправке (тип, вес, RAW), повторять нечем, а окончательный отказ (дубль, сцена,
      // обработка) ответит тем же — повтор зациклился бы на нём.
      const canRetry = (item: UploadItem): boolean =>
        retryable.has(item.fileId) && uploadFailureSeverity(item.failure) !== 'final';

      const failedItems = computed(() =>
        store
          .items()
          .filter(item => item.status === 'failed')
          .map(item => ({
            name: item.name,
            message: UPLOAD_FAILURE_MESSAGES[item.failure ?? 'unknown'],
            retryable: canRetry(item),
            previewUrl: item.previewUrl,
          })),
      );

      // Galleries whose EXIF drain already ran (or is running) this app session — the
      // drain is idempotent server-side, this just avoids re-walking it on every visit.
      const exifDrainStarted = new Set<string>();

      // Один байтовый счёт на всю живую пачку: файлы, докинутые посреди загрузки, идут своими
      // воркерами, но «155 из 220 МБ» складывается здесь, а не в каждом вызове (спека п. 8).
      const fileSize = new Map<string, number>();
      const filePercent = new Map<string, number>();
      const publishBytes = (generation: number): void => {
        if (store.generation() !== generation) return;
        let sent = 0;
        let total = 0;
        for (const [fileId, size] of fileSize) {
          total += size;
          sent += (size * (filePercent.get(fileId) ?? 0)) / 100;
        }

        updateState(store, '[UploadQueue] Upload Progress', {
          percent: total > 0 ? Math.round((sent / total) * 100) : null,
          bytesSent: Math.round(sent),
          bytesTotal: total,
        });
      };

      const runPoll = async (galleryId: string, generation: number): Promise<void> => {
        for (let pass = 0; pass < POLL_MAX_PASSES; pass += 1) {
          await wait(POLL_INTERVAL_MS);
          if (store.generation() !== generation) return;
          if (!store.items().some(item => item.status === 'processing')) return;

          const result = await data.syncStatus(galleryId);
          if (store.generation() !== generation) return;
          if (!result) continue;

          // Этот же ответ — единственный источник вердикта «ошибка» для плиток сетки.
          // Отдаём его сценам на КАЖДОМ проходе: иначе красная плитка с «Повторить»
          // появится только при следующем открытии галереи.
          manage.applyProcessingStatuses(galleryId, result.photos);

          const ready: string[] = [];
          const failed: string[] = [];
          for (const [photoId, entry] of Object.entries(result.photos)) {
            const fileId = photoToFile.get(photoId);
            if (!fileId) continue;
            if (entry.status === 'ready') ready.push(fileId);
            else if (entry.status === 'failed') failed.push(fileId);
          }

          if (ready.length > 0) {
            const ids = new Set(ready);
            updateState(store, '[UploadQueue] Photos Ready', {
              items: store
                .items()
                .map(item =>
                  ids.has(item.fileId) && item.status === 'processing' ? { ...item, status: 'ready' as const } : item,
                ),
              readyPulse: store.readyPulse() + 1,
            });
            void retireReplaced(galleryId, ready);
          }

          if (failed.length > 0) {
            for (const fileId of failed) replaceTargets.delete(fileId);
            const ids = new Set(failed);
            updateState(store, '[UploadQueue] Photos Failed', {
              items: store
                .items()
                .map(item =>
                  ids.has(item.fileId) && item.status === 'processing'
                    ? { ...item, status: 'failed' as const, failure: 'processing' as const }
                    : item,
                ),
            });
          }

          // Settled mid-pass: stop before sleeping another interval.
          if (!store.items().some(item => item.status === 'processing')) return;
        }

        // Poll budget exhausted — whatever is still processing is reported failed for
        // this session; a later sync pass on the server can still complete the photo.
        const stuck = store
          .items()
          .filter(item => item.status === 'processing')
          .map(item => item.fileId);
        if (stuck.length > 0) failItems('[UploadQueue] Poll Budget Exhausted', stuck, 'processing');
      };

      // Одна петля опроса на галерею: докинутая пачка присоединяется к идущей, а не удваивает
      // запросы sync-status.
      let activePoll: Promise<void> | null = null;
      const pollUntilSettled = (galleryId: string, generation: number): Promise<void> => {
        if (activePoll) return activePoll;
        activePoll = runPoll(galleryId, generation).finally(() => {
          activePoll = null;
        });
        return activePoll;
      };

      // Перезагрузка посреди обработки (спека п. 12): фото со статусом pending из первого
      // ответа sync-status становятся строками очереди — полоса видит их и доводит до финала.
      const seedProcessing = (galleryId: string, statuses: SyncResult['photos']): void => {
        if (store.active()) return;
        const known = new Set(store.items().map(item => item.photoId));
        const pending = Object.entries(statuses)
          .filter(([photoId, entry]) => entry.status === 'pending' && !known.has(photoId))
          .map(([photoId]) => photoId);
        if (pending.length === 0) return;
        const names = new Map(
          manage.scenes().flatMap(scene => scene.photos.map(photo => [photo.id, photo.originalName] as const)),
        );
        for (const photoId of pending) photoToFile.set(photoId, photoId);
        const settled = store.items().filter(isSettled);
        dropPreviews(settled);
        updateState(store, '[UploadQueue] Resume Processing', {
          galleryId,
          items: [
            ...store.items().filter(item => !isSettled(item)),
            ...pending.map(photoId => ({
              fileId: photoId,
              name: names.get(photoId) ?? photoId,
              status: 'processing' as const,
              photoId,
            })),
          ],
          landedPhotoIds: pending,
          paintedPhotoIds: [],
        });
        void pollUntilSettled(galleryId, store.generation());
      };

      const api = {
        failedItems,

        // Drives the server's bounded EXIF backfill to empty for a gallery, independent
        // of any upload: the upload poll only runs while a batch is processing, so without
        // this a gallery that never sees another upload would keep its pre-migration
        // photos out of camera search forever. Stops on the first pass that reports no
        // backlog, on a failed call, or at the pass bound.
        async ensureExifBackfill(galleryId: string): Promise<void> {
          // Одноразовый — только СЛИВ backfill: он идемпотентен на сервере, второй
          // проход по галерее ничего не даст. Чтение вердиктов обработки из того же
          // ответа нужно каждый раз, поэтому один запрос делаем всегда.
          const drained = exifDrainStarted.has(galleryId);
          exifDrainStarted.add(galleryId);
          for (let pass = 0; pass < EXIF_DRAIN_MAX_PASSES; pass += 1) {
            const result = await data.syncStatus(galleryId);
            // Тот же ответ несёт вердикт обработки по каждому незавершённому фото —
            // единственный источник «ошибки» для плиток сетки. Отдаём его сценам,
            // второго запроса ради этого не делаем.
            if (result) manage.applyProcessingStatuses(galleryId, result.photos);
            if (result && pass === 0) seedProcessing(galleryId, result.photos);
            if (drained || !result?.exifBacklog) return;
            await wait(EXIF_DRAIN_INTERVAL_MS);
          }
        },

        // Live-bus fast-path: the same processing→ready flip a poll pass does, driven
        // by a server push instead of the 2.5s tick. Poll stays the fallback.
        markPhotosReady(galleryId: string, photoIds: readonly string[]): void {
          if (store.galleryId() !== galleryId) return;
          const fileIds = photoIds.map(photoId => photoToFile.get(photoId)).filter((fileId): fileId is string => Boolean(fileId));
          const ids = new Set(fileIds);
          if (!store.items().some(item => ids.has(item.fileId) && item.status === 'processing')) return;
          updateState(store, '[UploadQueue] Photos Ready (live)', {
            items: store
              .items()
              .map(item => (ids.has(item.fileId) && item.status === 'processing' ? { ...item, status: 'ready' as const } : item)),
            readyPulse: store.readyPulse() + 1,
          });
          void retireReplaced(galleryId, fileIds);
        },

        /** Сетка нарисовала картинку фото пачки — один раз на фото, битая картинка тоже считается (спека п. 29). */
        markPainted(photoId: string): void {
          if (!store.landedPhotoIds().includes(photoId) || store.paintedPhotoIds().includes(photoId)) return;
          updateState(store, '[UploadQueue] Photo Painted', { paintedPhotoIds: [...store.paintedPhotoIds(), photoId] });
        },

        /**
         * Stops the batch: what is on the wire is aborted and the generation bump makes every pending
         * upload abandon at its next checkpoint, so nothing more is sent or registered.
         * A register answer that lands after the cancel is read, and the rows it created are deleted
         * so cancelled photos never surface. `ready` rows stay; `processing` rows are already uploaded
         * and registered — they leave the bar now and come back on the next open, where the server's
         * sync finishes them (spec п. 12).
         */
        cancel(): void {
          if (uploadKey !== null) data.cancelUploads(uploadKey);
          const kept = store.items().filter(item => item.status === 'ready');
          dropPreviews(store.items().filter(item => item.status !== 'ready'));
          retryable.clear();
          replaceTargets.clear();
          fileSize.clear();
          filePercent.clear();
          updateState(store, '[UploadQueue] Cancel', {
            generation: store.generation() + 1,
            items: kept,
            percent: null,
            bytesSent: 0,
            bytesTotal: 0,
          });
        },

        clearFinished(): void {
          const kept = store.items().filter(item => !isSettled(item));
          dropPreviews(store.items().filter(item => !kept.includes(item)));
          updateState(store, '[UploadQueue] Clear Finished', { items: kept });
        },

        async uploadFiles(
          galleryId: string,
          files: readonly File[],
          sceneId?: string,
          // keepFailed — повтор из окна отказов: красные строки, которые не повторяются, остаются.
          // replaces — «Перезаписать», перенесённое повтором: файл → фото, которые он заменяет.
          opts?: { keepFailed?: boolean; replaces?: ReadonlyMap<File, readonly string[]> },
        ): Promise<UploadBatchOutcome> {
          if (files.length === 0) return 'queued';
          // Повтор из окна отказов гоняет файлы, которых в галерее нет, — вопрос там лишний.
          // Без совпавших имён ветка с `await` не берётся вовсе: бросок посреди живой пачки
          // должен встать в неё тем же тиком, иначе он заводит вторую пачку.
          const known = opts?.keepFailed ? new Set<string>() : knownNames();
          const hasDuplicates = files.some(file => known.has(file.name));
          const accepted = hasDuplicates ? await askAboutDuplicates(files, known) : files;
          if (accepted.length === 0) return 'queued';
          // «Перезаписать»: the gallery's photos under the names being replaced, taken before any new one lands.
          const replaced = new Set(accepted.filter(file => known.has(file.name)).map(file => file.name));
          const replacedPhotos = manage
            .scenes()
            .flatMap(scene => scene.photos)
            .filter(photo => photo.originalName && replaced.has(photo.originalName));
          // Файлы, докинутые посреди загрузки, встают в ту же пачку: поколение не меняется,
          // объём растёт, полоса откатывается к новой доле (спека п. 8). Пачка ДРУГОЙ галереи
          // на живую не садится — у полосы одна галерея, и «живая» включает фазу processing
          // (до 10 минут): бросок молча выбросить нельзя — исход возвращается вызывающему,
          // чтобы страница сказала фотографу, куда сейчас идёт загрузка.
          const append = store.active();
          if (append && store.galleryId() !== galleryId) return 'busy_other_gallery';
          const generation = append ? store.generation() : store.generation() + 1;
          // Client-side pre-check: wrong-type / over-limit files fail instantly with the
          // exact reason rather than round-tripping to a generic server error (spec §3.4/§5).
          const batch: Array<{ fileId: string; file: File }> = [];
          const preRejected: Array<{ fileId: string; name: string; reason: UploadFailureReason; previewUrl?: string }> = [];
          // Естественный порядок имён: сцена принимает пачку так же, как фотограф видит её в папке,
          // и хвост плиток в полёте идёт тем же порядком, что и приходящие фото.
          for (const file of sortFilesNaturally(accepted)) {
            const fileId = crypto.randomUUID();
            const reason = validateUploadFile(file);
            if (reason) preRejected.push({ fileId, name: file.name, reason, previewUrl: localPreview(file) });
            else batch.push({ fileId, file });
          }

          // A new batch replaces finished items; an appended one keeps every row of the live batch.
          const carried = append
            ? store.items()
            : store.items().filter(item => !isSettled(item) || (opts?.keepFailed && item.status === 'failed'));
          dropPreviews(store.items().filter(item => !carried.includes(item)));
          if (!append) {
            fileSize.clear();
            filePercent.clear();
          }

          const scene = sceneId ? { sceneId } : {};
          updateState(store, append ? '[UploadQueue] Batch Append' : '[UploadQueue] Batch Start', {
            galleryId,
            generation,
            ...(append ? {} : { landedPhotoIds: [], paintedPhotoIds: [], percent: null, bytesSent: 0, bytesTotal: 0 }),
            items: [
              ...carried,
              ...preRejected.map(entry => ({
                fileId: entry.fileId,
                name: entry.name,
                status: 'failed' as const,
                failure: entry.reason,
                ...scene,
                ...(entry.previewUrl ? { previewUrl: entry.previewUrl } : {}),
              })),
              ...batch.map(entry => {
                const previewUrl = localPreview(entry.file);
                return {
                  fileId: entry.fileId,
                  name: entry.file.name,
                  status: 'uploading' as const,
                  ...scene,
                  ...(previewUrl ? { previewUrl } : {}),
                };
              }),
            ],
          });
          // Every file was rejected client-side — nothing to send.
          if (batch.length === 0) return 'queued';
          // Held before the first request: a batch that dies on its project step fails every
          // file at once, and «Повторить всё» must still have them to re-send.
          for (const entry of batch) {
            retryable.set(entry.fileId, entry.file);
            const targets =
              opts?.replaces?.get(entry.file) ??
              replacedPhotos.filter(photo => photo.originalName === entry.file.name).map(photo => photo.id);
            if (targets.length > 0) replaceTargets.set(entry.fileId, targets);
          }

          const project = await data.ensureProject(galleryId);
          if (store.generation() !== generation) return 'queued';
          if (!project) {
            failItems(
              '[UploadQueue] Project Failed',
              batch.map(entry => entry.fileId),
              'unknown',
            );
            return 'queued';
          }

          uploadKey = project.lumirProjectId;

          // RAW gate (spec 15 §13 F5): the pre-check admits RAW syntactically; the
          // account flag decides here, before any RAW bytes leave the browser.
          let sendable = batch;
          if (!project.rawUploadEnabled) {
            const rawEntries = batch.filter(entry => isRawFile(entry.file));
            if (rawEntries.length > 0) {
              failItems(
                '[UploadQueue] RAW Disabled',
                rawEntries.map(entry => entry.fileId),
                'raw_disabled',
              );
              sendable = batch.filter(entry => !isRawFile(entry.file));
              if (sendable.length === 0) return 'queued';
            }
          }

          // Consent gate: galleries with face recognition off must not create persons.
          // Sharpening: the account's web-preview level rides each upload (spec 02 §5).
          const uploadOpts = { notGenerateNewPersons: !project.faceRecognitionEnabled, sharpening: project.sharpeningLevel };

          // One honest percent for the whole batch: each file's own byte progress weighted
          // by its size, so a 40 MB photo does not read the same as a 2 MB one. A retried
          // file legitimately walks its share back — the bar shows the wire, not a guess.
          for (const entry of sendable) fileSize.set(entry.fileId, entry.file.size);

          publishBytes(generation);
          const publishPercent = (fileId: string, percent: number): void => {
            if (store.generation() !== generation) return;
            filePercent.set(fileId, percent);
            publishBytes(generation);
          };

          // Row id → the id the platform stored the file under (reuseDuplicate can hand back the
          // project's existing copy): register names the stored id, the answers map back to rows.
          const uploaded: Array<{ fileId: string; file: File; storedId: string }> = [];
          // The whole set goes to the engine at once: its slots, not this store, keep the wire from flooding.
          await Promise.all(
            sendable.map(async entry => {
              if (store.generation() !== generation) return;
              const result = await data.upload(project.lumirProjectId, entry.file, entry.fileId, {
                ...uploadOpts,
                onProgress: percent => publishPercent(entry.fileId, percent),
              });
              if (store.generation() !== generation) return;
              if (result.ok) {
                const storedId = result.fileId ?? entry.fileId;
                publishPercent(entry.fileId, 100);
                // Two rows resolved to one stored file: the later row is the duplicate, never a second register.
                if (uploaded.some(item => item.storedId === storedId)) {
                  failItems('[UploadQueue] File Duplicate In Batch', [entry.fileId], 'duplicate');
                } else {
                  uploaded.push({ ...entry, storedId });
                  setItemStatus('[UploadQueue] File Uploaded', [entry.fileId], 'registering');
                }
              } else {
                failItems('[UploadQueue] File Upload Failed', [entry.fileId], result.reason);
              }
            }),
          );
          if (store.generation() !== generation) return 'queued';
          // Nothing of THIS call is on the wire any more; a sibling append may still be sending.
          if (!store.items().some(item => item.status === 'uploading')) {
            updateState(store, '[UploadQueue] Upload Phase Done', { percent: null });
          }

          if (uploaded.length === 0) return 'queued';

          // Integrity metadata (size/mime/hash) is server-derived at register time —
          // the browser sends only identity + the display name.
          const rowByStoredId = new Map(uploaded.map(entry => [entry.storedId, entry.fileId]));
          const registerInputs = uploaded.map(entry => ({
            fileId: entry.storedId,
            ...scene,
            originalName: entry.file.name,
            ...fileModifiedAtPayload(entry.file),
          }));

          // The server caps one register call, so a big batch registers in slices. Each slice
          // stands alone: a refused slice fails only its own files (the retry re-sends exactly
          // them), the others carry on, and the outcomes of every slice merge into one verdict.
          const outcomes: RegisterOutcome[] = [];
          const refused: string[] = [];
          for (let index = 0; index < registerInputs.length; index += REGISTER_BATCH_LIMIT) {
            const slice = registerInputs.slice(index, index + REGISTER_BATCH_LIMIT);
            const sliceOutcomes = await data.register(galleryId, slice);
            if (sliceOutcomes) outcomes.push(...sliceOutcomes);
            if (store.generation() !== generation) {
              // «Отменить» landed while register was on the wire: the server has just created rows the
              // bar will never show, and the next open would resurrect them as photos she cancelled.
              // Exactly those rows — the ids the responses so far report as created, never a blind
              // sweep — are deleted; a failure here leaves them for the next open, the pre-cancel state.
              const created = outcomes.filter(o => o.status === 'created' && o.photoId).map(o => o.photoId as string);
              if (created.length > 0) await manageData.deletePhotos(galleryId, created, true).catch(() => undefined);
              return 'queued';
            }

            if (!sliceOutcomes) refused.push(...slice.map(input => rowByStoredId.get(input.fileId) as string));
          }

          if (refused.length > 0) failItems('[UploadQueue] Register Failed', refused, 'unknown');
          if (refused.length === uploaded.length) return 'queued';

          // RAW rows ride the same readiness poll: the server's sync flips a RAW row
          // ready the moment the trusted gateway listing yields its original_url (no
          // renditions ever, and probes never fail a RAW row), so the tile turns
          // ready only when the download genuinely works.
          const processing: string[] = [];
          const mismatched: string[] = [];
          const duplicated: string[] = [];
          const fileToPhoto = new Map<string, string>();
          for (const outcome of outcomes) {
            // Answers name the stored id; the queue row is the browser's. An answer for an id no
            // row of this batch sent is not ours to show.
            const rowId = rowByStoredId.get(outcome.fileId);
            if (!rowId) continue;
            if (outcome.status === 'created' && outcome.photoId) {
              photoToFile.set(outcome.photoId, rowId);
              fileToPhoto.set(rowId, outcome.photoId);
              processing.push(rowId);
            } else if (outcome.status === 'duplicate') {
              // `already_registered`: already a photo of this gallery, not a photo this batch added.
              duplicated.push(rowId);
            } else if (outcome.status === 'scene_mismatch') {
              mismatched.push(rowId);
            }
          }

          // Фото пачки названы — их плитки в сетке имеют право проявиться (600 мс), остальные нет.
          updateState(store, '[UploadQueue] Photos Landed', {
            landedPhotoIds: [...store.landedPhotoIds(), ...fileToPhoto.values()],
            items: store.items().map(item => {
              const photoId = fileToPhoto.get(item.fileId);
              return photoId ? { ...item, photoId } : item;
            }),
          });

          if (mismatched.length > 0) failItems('[UploadQueue] Scene Mismatch', mismatched, 'scene_mismatch');
          if (duplicated.length > 0) failItems('[UploadQueue] Already Registered', duplicated, 'duplicate');
          // `refused` (for example: not a file of this gallery's project, held by another gallery or by
          // another place under the same id, its path queued for deletion, a RAW file while RAW uploads
          // are off) and anything unanswered: an error the photographer can read,
          // never a silent success.
          const rejected = uploaded
            .map(entry => entry.fileId)
            .filter(
              fileId =>
                !processing.includes(fileId) &&
                !mismatched.includes(fileId) &&
                !duplicated.includes(fileId) &&
                !refused.includes(fileId),
            );
          if (rejected.length > 0) failItems('[UploadQueue] Register Rejected', rejected, 'unknown');

          if (processing.length === 0) return 'queued';
          setItemStatus('[UploadQueue] Registered', processing, 'processing');

          await pollUntilSettled(galleryId, generation);
          return 'queued';
        },

        // A native video file is NOT a gallery photo: it uploads the raw bytes, then the
        // uploader's own public static URL becomes the video row's source (T3 create).
        // No register + no readiness poll — the image fleet never processes it, so it
        // would sit 'processing' forever. The row flips uploading→ready the moment the
        // upload returns a usable URL; the caller turns that URL into a video scene.
        async uploadVideo(galleryId: string, file: File): Promise<UploadVideoResult> {
          if (store.active()) return { ok: false, reason: 'unknown' };
          const syncReason = validateUploadFile(file);
          const dimReason = syncReason ? null : await validateVideoDimensions(file);
          const reason = syncReason ?? dimReason;
          const fileId = crypto.randomUUID();
          const generation = store.generation() + 1;
          updateState(store, '[UploadQueue] Video Start', {
            galleryId,
            generation,
            percent: null,
            items: [
              ...store.items().filter(item => !isSettled(item)),
              {
                fileId,
                name: file.name,
                status: reason ? ('failed' as const) : ('uploading' as const),
                ...(reason ? { failure: reason } : {}),
              },
            ],
          });
          if (reason) return { ok: false, reason };

          const project = await data.ensureProject(galleryId);
          if (store.generation() !== generation) return { ok: false, reason: 'unknown' };
          if (!project) {
            failItems('[UploadQueue] Video Project Failed', [fileId], 'unknown');
            return { ok: false, reason: 'unknown' };
          }

          const result = await data.upload(project.lumirProjectId, file, fileId, {
            notGenerateNewPersons: !project.faceRecognitionEnabled,
            // A native video is the single biggest thing a photographer sends — the bar
            // carries its own byte percent, with no batch to average against.
            onProgress: percent => {
              if (store.generation() === generation && percent !== store.percent()) {
                updateState(store, '[UploadQueue] Video Progress', { percent });
              }
            },
          });
          if (store.generation() !== generation) return { ok: false, reason: 'unknown' };
          updateState(store, '[UploadQueue] Video Upload Done', { percent: null });
          if (!result.ok) {
            failItems('[UploadQueue] Video Upload Failed', [fileId], result.reason);
            return { ok: false, reason: result.reason };
          }

          if (!result.fileUrl) {
            failItems('[UploadQueue] Video URL Missing', [fileId], 'unknown');
            return { ok: false, reason: 'unknown' };
          }

          setItemStatus('[UploadQueue] Video Ready', [fileId], 'ready');
          return { ok: true, fileUrl: result.fileUrl, fileId: result.fileId ?? fileId };
        },

        // Upload a captured video frame as a gallery photo and return its photoId, so the
        // caller can set it as the video's poster (Option A: a frame IS a normal photo —
        // it earns thumbnail/small/medium renditions through the standard fleet). No queue
        // row + no readiness poll: register returns the photoId synchronously, which is all
        // setVideoPoster needs; the fleet fills the thumbnail moments later like any photo.
        async uploadPosterFrame(galleryId: string, blob: Blob): Promise<string | null> {
          const project = await data.ensureProject(galleryId);
          if (!project) return null;
          const fileId = crypto.randomUUID();
          const file = new File([blob], `poster-${fileId}.jpg`, { type: 'image/jpeg' });
          const uploaded = await data.upload(project.lumirProjectId, file, fileId, {
            notGenerateNewPersons: !project.faceRecognitionEnabled,
            sharpening: project.sharpeningLevel,
          });
          if (!uploaded.ok) return null;
          const storedId = uploaded.fileId ?? fileId;
          const outcomes = await data.register(galleryId, [{ fileId: storedId, originalName: file.name }]);
          const outcome = outcomes?.find(item => item.fileId === storedId);
          return outcome?.status === 'created' ? outcome.photoId : null;
        },

        /**
         * «Повторить всё» from the failure window — the same files, re-sent into their scenes.
         * Only rows whose file was ever accepted for sending go: a file refused by type or
         * size has nothing to retry and keeps its red row.
         */
        retryAll(): void {
          const galleryId = store.galleryId();
          if (!galleryId) return;
          const retried = store.items().filter(item => item.status === 'failed' && canRetry(item));
          if (retried.length === 0) return;
          const ids = new Set(retried.map(item => item.fileId));
          dropPreviews(retried);
          updateState(store, '[UploadQueue] Retry All', { items: store.items().filter(item => !ids.has(item.fileId)) });
          const byScene = new Map<string | undefined, File[]>();
          const replaces = new Map<File, readonly string[]>();
          for (const item of retried) {
            const file = retryable.get(item.fileId);
            if (!file) continue;
            byScene.set(item.sceneId, [...(byScene.get(item.sceneId) ?? []), file]);
            const targets = replaceTargets.get(item.fileId);
            if (targets) replaces.set(file, targets);
            replaceTargets.delete(item.fileId);
          }

          for (const [sceneId, files] of byScene) void api.uploadFiles(galleryId, files, sceneId, { keepFailed: true, replaces });
        },
      };

      return api;
    },
  ),
);
